import { Component, HostListener, Input } from '@angular/core';
import { UserInfo } from '@memberjunction/core';
import { MessageAttachmentChip } from './message-attachments.component';

/**
 * Right-side slide-in drawer for previewing one attachment via MJ's
 * <mj-artifact-viewer-panel>. Holds a list of sibling attachments so
 * the user can flip through with prev/next.
 *
 * Closes on: × button, ESC key, backdrop click. Switching messages
 * (parent unmounts/recreates the message-viewer) closes implicitly.
 */
@Component({
    standalone: false,
    selector: 'app-attachment-drawer',
    templateUrl: './attachment-drawer.component.html',
    styleUrls: ['./attachment-drawer.component.css']
})
export class AttachmentDrawerComponent {
    @Input() currentUser!: UserInfo;
    @Input() environmentId!: string;

    protected isOpen = false;
    protected chips: MessageAttachmentChip[] = [];
    protected currentIndex = 0;

    public openWith(chips: MessageAttachmentChip[], index: number): void {
        this.chips = chips;
        this.currentIndex = Math.max(0, Math.min(index, chips.length - 1));
        this.isOpen = true;
    }

    public close(): void {
        this.isOpen = false;
    }

    /** True while the attachment preview drawer is open. */
    public get isDrawerOpen(): boolean {
        return this.isOpen;
    }

    protected next(): void {
        if (this.currentIndex < this.chips.length - 1) {
            this.currentIndex++;
        }
    }

    protected previous(): void {
        if (this.currentIndex > 0) {
            this.currentIndex--;
        }
    }

    protected get currentChip(): MessageAttachmentChip | null {
        return this.chips[this.currentIndex] ?? null;
    }

    @HostListener('document:keydown.escape')
    protected onEscape(): void {
        if (this.isOpen) this.close();
    }

    @HostListener('document:keydown.arrowright', ['$event'])
    protected onArrowRight(event: KeyboardEvent): void {
        if (this.isOpen) {
            event.preventDefault();
            this.next();
        }
    }

    @HostListener('document:keydown.arrowleft', ['$event'])
    protected onArrowLeft(event: KeyboardEvent): void {
        if (this.isOpen) {
            event.preventDefault();
            this.previous();
        }
    }
}
